import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TransactionMatchingInterface = ({ transactions, onMatch, onUpdateStatus, loading }) => {
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showMatchModal, setShowMatchModal] = useState(false);

  const handleSelectTransaction = (transactionId) => {
    if (selectedTransactions?.includes(transactionId)) {
      setSelectedTransactions(selectedTransactions?.filter(id => id !== transactionId));
    } else {
      if (selectedTransactions?.length < 2) {
        setSelectedTransactions([...selectedTransactions, transactionId]);
      }
    }
  };

  const handleMatchSelected = () => {
    if (selectedTransactions?.length === 2) {
      onMatch?.(selectedTransactions?.[0], selectedTransactions?.[1]);
      setSelectedTransactions([]);
      setShowMatchModal(false);
    }
  };

  const filteredTransactions = filterType === 'all'
    ? transactions
    : transactions?.filter(t => t?.transaction_type === filterType);

  const getTypeIcon = (type) => {
    const icons = {
      pos_sale: 'ShoppingCart',
      membership_payment: 'CreditCard',
      billing_record: 'FileText',
      bank_deposit: 'Building2'
    };
    return icons?.[type] || 'Circle';
  };

  const getTypeColor = (type) => {
    const colors = {
      pos_sale: 'text-primary',
      membership_payment: 'text-success',
      billing_record: 'text-warning',
      bank_deposit: 'text-info'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  const getStatusBadge = (status) => {
    const badges = {
      matched: 'bg-success/10 text-success',
      unmatched: 'bg-warning/10 text-warning',
      disputed: 'bg-destructive/10 text-destructive',
      resolved: 'bg-primary/10 text-primary',
      pending_review: 'bg-muted text-muted-foreground'
    };
    return badges?.[status] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Transaction Matching</h3>
          <p className="text-sm text-muted-foreground">Select two transactions to match and reconcile</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={filterType}
            onChange={(value) => setFilterType(value)}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'pos_sale', label: 'POS Sales' },
              { value: 'membership_payment', label: 'Membership Payments' },
              { value: 'bank_deposit', label: 'Bank Deposits' }
            ]}
            className="w-48"
          />
          {selectedTransactions?.length === 2 && (
            <Button onClick={handleMatchSelected} iconName="Link" iconPosition="left">
              Match Selected
            </Button>
          )}
        </div>
      </div>
      {selectedTransactions?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Info" className="text-primary" size={18} />
            <p className="text-sm text-primary font-medium">
              {selectedTransactions?.length} transaction(s) selected. 
              {selectedTransactions?.length === 1 && ' Select one more to match.'}
              {selectedTransactions?.length === 2 && ' Click "Match Selected" to reconcile.'}
            </p>
          </div>
        </div>
      )}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredTransactions?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="FileSearch" size={48} className="mx-auto mb-3" />
            <p>No transactions found</p>
          </div>
        ) : (
          filteredTransactions?.map((transaction) => (
            <div
              key={transaction?.id}
              className={`border rounded-lg p-4 transition-all cursor-pointer ${
                selectedTransactions?.includes(transaction?.id)
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:shadow-sm'
              }`}
              onClick={() => transaction?.reconciliation_status === 'unmatched' && handleSelectTransaction(transaction?.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-10 h-10 rounded-full bg-accent flex items-center justify-center ${getTypeColor(transaction?.transaction_type)}`}>
                    <Icon name={getTypeIcon(transaction?.transaction_type)} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{transaction?.transaction_number}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(transaction?.reconciliation_status)}`}>
                        {transaction?.reconciliation_status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground capitalize mb-2">
                      {transaction?.transaction_type?.replace('_', ' ')} • {transaction?.payment_method?.replace('_', ' ')}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {new Date(transaction?.transaction_date)?.toLocaleDateString()}
                      </span>
                      {transaction?.amount_difference !== 0 && (
                        <span className="text-warning flex items-center gap-1">
                          <Icon name="AlertCircle" size={14} />
                          Variance: ${Math.abs(transaction?.amount_difference)?.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">${parseFloat(transaction?.amount)?.toFixed(2)}</p>
                  {transaction?.reconciliation_status === 'unmatched' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onUpdateStatus?.(transaction?.id, 'pending_review');
                      }}
                      className="mt-2"
                    >
                      Review
                    </Button>
                  )}
                </div>
              </div>
              {transaction?.metadata && Object.keys(transaction?.metadata)?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    {Object.entries(transaction?.metadata)?.map(([key, value]) => (
                      <span key={key} className="mr-3">
                        {key}: {value?.toString()}
                      </span>
                    ))}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TransactionMatchingInterface;